// src/components/common/BulkUploadModal.js
import React, { useState, useRef } from 'react';
import { Download, Upload, CheckCircle, AlertTriangle, FileSpreadsheet, X } from 'lucide-react';
import api from '../../services/api';
import { Modal, Spinner } from './index';
import toast from 'react-hot-toast';

export default function BulkUploadModal({ open, onClose, onSuccess, type, title }) {
  const [file,     setFile]    = useState(null);
  const [loading,  setLoading] = useState(false);
  const [result,   setResult]  = useState(null);
  const inputRef = useRef();

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/templates/download/${type}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `${title.replace(/[^a-zA-Z0-9]/g,'_')}_Template.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Template download failed'); }
  };

  const handleFile = e => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.match(/\.(xlsx|xls|csv)$/i)) { toast.error('Please upload an Excel (.xlsx/.xls) or CSV file'); return; }
    setFile(f); setResult(null);
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Please select a file first'); return; }
    setLoading(true); setResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const r = await api.post(`/templates/upload/${type}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
      if (r.data.data?.success > 0) {
        toast.success(`${r.data.data.success} records imported successfully`);
        if (r.data.data.errors?.length === 0) { setTimeout(() => { onSuccess?.(); handleClose(); }, 1500); }
      }
    } catch (err) {
      // The server's message is already shown by the API error handler (services/api.js).
    } finally { setLoading(false); }
  };

  const handleClose = () => { setFile(null); setResult(null); onClose(); };

  const errors  = result?.data?.errors  || [];
  const success = result?.data?.success || 0;

  return (
    <Modal open={open} onClose={handleClose} title={`Bulk Upload — ${title}`} size="md"
      footer={
        <div style={{ display:'flex', justifyContent:'space-between', width:'100%', alignItems:'center' }}>
          <button className="btn btn-secondary" onClick={downloadTemplate}><Download size={13}/> Download Template</button>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-secondary" onClick={handleClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleUpload} disabled={loading || !file}>
              {loading ? <><Spinner size="sm"/> Importing…</> : <><Upload size={13}/> Import</>}
            </button>
          </div>
        </div>
      }>

      {/* Step 1 — Download template */}
      <div style={{ background:'var(--primary-light)', border:'1px solid var(--primary)', borderRadius:'var(--r-md)', padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'flex-start', gap:12 }}>
        <div style={{ background:'var(--primary)', color:'#fff', borderRadius:'50%', width:22, height:22, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:12, flexShrink:0, marginTop:1 }}>1</div>
        <div>
          <div style={{ fontWeight:600, fontSize:'var(--fs-sm)', color:'var(--primary)', marginBottom:4 }}>Download the template</div>
          <div style={{ fontSize:'var(--fs-xs)', color:'var(--text-secondary)', marginBottom:8 }}>Use the template Excel file to fill your data. It has sample rows, required fields marked *, and an Instructions sheet.</div>
          <button className="btn btn-secondary btn-sm" onClick={downloadTemplate}><Download size={12}/> Download {title} Template.xlsx</button>
        </div>
      </div>

      {/* Step 2 — Upload filled file */}
      <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'flex-start', gap:12 }}>
        <div style={{ background:'var(--text-muted)', color:'#fff', borderRadius:'50%', width:22, height:22, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:12, flexShrink:0, marginTop:1 }}>2</div>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:600, fontSize:'var(--fs-sm)', marginBottom:4 }}>Upload your filled file</div>
          <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display:'none' }} onChange={handleFile} />
          {!file ? (
            <div onClick={() => inputRef.current?.click()}
              style={{ border:'1.5px dashed var(--border)', borderRadius:'var(--r-md)', padding:'24px 16px', textAlign:'center', cursor:'pointer', transition:'border-color .2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor='var(--primary)'}
              onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}>
              <FileSpreadsheet size={28} color="var(--text-muted)" style={{ margin:'0 auto 8px' }}/>
              <div style={{ fontSize:'var(--fs-sm)', fontWeight:500, marginBottom:4 }}>Click to select file</div>
              <div style={{ fontSize:'var(--fs-xs)', color:'var(--text-muted)' }}>Supports .xlsx, .xls, .csv</div>
            </div>
          ) : (
            <div style={{ display:'flex', alignItems:'center', gap:10, background:'var(--white)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'10px 14px' }}>
              <FileSpreadsheet size={20} color="var(--accent)" />
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:500, fontSize:'var(--fs-sm)' }}>{file.name}</div>
                <div style={{ fontSize:'var(--fs-xs)', color:'var(--text-muted)' }}>{(file.size/1024).toFixed(1)} KB</div>
              </div>
              <button type="button" onClick={() => { setFile(null); setResult(null); inputRef.current.value=''; }}
                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:4 }}><X size={15}/></button>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      {result && (
        <div>
          {success > 0 && (
            <div style={{ background:'var(--accent-light)', border:'1px solid var(--accent)', borderRadius:'var(--r-md)', padding:'10px 14px', marginBottom:8, display:'flex', alignItems:'center', gap:10 }}>
              <CheckCircle size={18} color="var(--accent)"/>
              <span style={{ fontWeight:600, fontSize:'var(--fs-sm)', color:'var(--accent)' }}>{success} record{success !== 1 ? 's' : ''} imported successfully</span>
            </div>
          )}
          {errors.length > 0 && (
            <div>
              <div style={{ background:'#FEF3E2', border:'1px solid #B56A00', borderRadius:'var(--r-md)', padding:'10px 14px', marginBottom:8, display:'flex', alignItems:'center', gap:10 }}>
                <AlertTriangle size={16} color="#B56A00"/>
                <span style={{ fontWeight:600, fontSize:'var(--fs-sm)', color:'#B56A00' }}>{errors.length} row{errors.length !== 1 ? 's' : ''} had errors</span>
              </div>
              <div style={{ maxHeight:180, overflowY:'auto', border:'1px solid var(--border-light)', borderRadius:'var(--r-md)' }}>
                {errors.slice(0,50).map((e, i) => (
                  <div key={i} style={{ padding:'7px 12px', borderBottom:'1px solid var(--border-light)', fontSize:'var(--fs-xs)', display:'flex', gap:10 }}>
                    <span style={{ fontFamily:'monospace', fontWeight:600, color:'var(--text-muted)', minWidth:50 }}>Row {e.row}</span>
                    <span style={{ color:'var(--danger)' }}>{e.error}</span>
                  </div>
                ))}
                {errors.length > 50 && <div style={{ padding:'7px 12px', fontSize:'var(--fs-xs)', color:'var(--text-muted)', textAlign:'center' }}>…and {errors.length-50} more errors</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
